/**
 * CreateChallengeOverlay - Streamlined challenge creation
 * 
 * Design philosophy: A challenge is a question. Title first, everything else optional.
 * The type grid is demoted to optional pills. Details expand on demand.
 * Amber accent language signals "challenge territory" across all challenge overlays.
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, Target, AlertCircle, ChevronDown, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';
import { challengesAPI, api } from '../../api';

// Challenge types as compact pills (optional, not a gate)
const CHALLENGE_TYPES = [
  { id: 'design', label: 'Design' },
  { id: 'ideas', label: 'Ideas' },
  { id: 'code_review', label: 'Code Review' },
  { id: 'debugging', label: 'Debugging' },
  { id: 'proofreading', label: 'Proofread' },
  { id: 'testing', label: 'Testing' },
  { id: 'optimization', label: 'Optimization' },
  { id: 'documentation', label: 'Docs' },
  { id: 'general', label: 'General' },
];

// Auto-infer type from title keywords
function inferChallengeType(text) {
  const lower = (text || '').toLowerCase();
  if (/color|font|design|look|style|layout|ui|ux|visual|icon|logo/i.test(lower)) return 'design';
  if (/review|check|feedback|proofread|grammar|spelling/i.test(lower)) return 'code_review';
  if (/fix|bug|error|broken|crash|issue|wrong/i.test(lower)) return 'debugging';
  if (/faster|slow|optim|perform|efficient/i.test(lower)) return 'optimization';
  if (/test|try|work|usab/i.test(lower)) return 'testing';
  if (/doc|readme|explain|write|comment/i.test(lower)) return 'documentation';
  if (/idea|suggest|brain|approach|better|improv|what.*should/i.test(lower)) return 'ideas';
  return 'general';
}

export default function CreateChallengeOverlay({ project, editChallenge, onClose, onSuccess }) {
  const isEditMode = !!editChallenge;

  const [title, setTitle] = useState(editChallenge?.challenge_title || editChallenge?.challengeTitle || '');
  const [description, setDescription] = useState(editChallenge?.challenge_description || editChallenge?.challengeDescription || '');
  const [challengeType, setChallengeType] = useState(editChallenge?.challenge_type || editChallenge?.challengeType || '');
  const [skillsInput, setSkillsInput] = useState(
    (editChallenge?.skills_needed || editChallenge?.skillsNeeded || []).join(', ')
  );
  const [showDetails, setShowDetails] = useState(isEditMode); // expand details in edit mode
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const titleRef = useRef(null);
  const overlayRef = useRef(null);
  const descRef = useRef(null);

  // Auto-focus title
  useEffect(() => {
    const timer = setTimeout(() => titleRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, []);

  // Focus description when details expand
  useEffect(() => {
    if (showDetails) {
      const timer = setTimeout(() => descRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [showDetails]);

  // Click outside / Escape to close
  useEffect(() => {
    const handleClick = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Give your challenge a title');
      titleRef.current?.focus();
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const skills = skillsInput
        ? skillsInput.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      if (isEditMode) {
        // ── Edit existing challenge ──
        const updateData = {
          challenge_title: trimmedTitle,
          challenge_description: description.trim() || undefined,
          challenge_type: challengeType || inferChallengeType(trimmedTitle),
          skills_needed: skills,
        };
        await api.put(`/challenges/${editChallenge.id}`, updateData);
        toast.success('Challenge updated!');
        onSuccess?.({ ...editChallenge, ...updateData });
        onClose();
      } else {
        // ── Create new challenge ──
        const challengeData = {
          project_id: project.id,
          challenge_type: challengeType || inferChallengeType(trimmedTitle),
          challenge_title: trimmedTitle,
          challenge_description: description.trim() || undefined,
          skills_needed: skills,
        };
        const response = await challengesAPI.create(challengeData);
        toast.success('Challenge posted!');
        onSuccess?.(response.data.challenge || response.data);
        onClose();
      }
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} challenge:`, err);
      setError(
        err.response?.data?.msg ||
        err.response?.data?.error ||
        'Something went wrong. Try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enter to submit from title (when details are collapsed)
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter' && !showDetails) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Ctrl/Cmd+Enter to submit from anywhere
  const handleGlobalKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const projectImage = (project || editChallenge?.project)?.image_url || (project || editChallenge?.project)?.imageUrl;
  const projectName = (project || editChallenge?.project)?.name;
  const projectInitial = projectName?.[0]?.toUpperCase() || '?';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        ref={overlayRef}
        onKeyDown={handleGlobalKeyDown}
        className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'challengeOverlayIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* ── Amber accent bar ── */}
        <div className="h-[3px] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600" />

        {/* ── Header ── */}
        <div className="flex items-start justify-between px-6 pt-5 pb-0">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Target className="w-4 h-4 text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">{isEditMode ? 'Edit Challenge' : 'New Challenge'}</h2>
            </div>
            {/* Project context chip */}
            <div className="flex items-center gap-1.5 ml-[42px]">
              <span className="text-[11px] text-zinc-500 tracking-wide uppercase">on</span>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-zinc-800/80 border border-zinc-700/50 rounded-md max-w-[260px]">
                {projectImage ? (
                  <img src={projectImage} alt="" className="w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-sm bg-zinc-700 flex items-center justify-center text-[8px] text-zinc-400 flex-shrink-0 font-medium">
                    {projectInitial}
                  </div>
                )}
                <span className="text-xs text-zinc-300 truncate">{projectName}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors mt-0.5"
          >
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="px-6 pt-5 pb-2">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* ── Hero title input ── */}
          <div className="mb-3">
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); if (error) setError(null); }}
              onKeyDown={handleTitleKeyDown}
              placeholder="What do you need help with?"
              maxLength={200}
              disabled={isSubmitting}
              autoComplete="off"
              spellCheck="true"
              className={cn(
                'w-full bg-transparent text-white text-lg font-medium leading-relaxed',
                'placeholder:text-zinc-600',
                'border-0 outline-none ring-0',
                'pb-3 border-b border-zinc-800 focus:border-amber-500/40',
                'transition-colors duration-300'
              )}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-zinc-600 tabular-nums">{title.length}/200</span>
              {!showDetails && title.length > 0 && (
                <span className="text-[11px] text-zinc-600">
                  Press <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-500 text-[10px] font-mono">Enter</kbd> to post
                </span>
              )}
            </div>
          </div>

          {/* ── Expand details toggle ── */}
          {!showDetails && (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors py-1 mb-1 group"
            >
              <Plus className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
              <span>Add details, skills, or category</span>
            </button>
          )}

          {/* ── Expanded details section ── */}
          {showDetails && (
            <div
              className="space-y-4 pb-1"
              style={{ animation: 'detailsExpand 0.2s ease-out' }}
            >
              {/* Description */}
              <div>
                <label className="block text-[11px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">
                  Details
                </label>
                <textarea
                  ref={descRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide more context about what you're looking for..."
                  rows={3}
                  disabled={isSubmitting}
                  className={cn(
                    'w-full bg-zinc-800/40 border border-zinc-700/50 rounded-xl px-4 py-3',
                    'text-sm text-white placeholder:text-zinc-600',
                    'focus:outline-none focus:ring-1 focus:ring-amber-500/30 focus:border-amber-500/30',
                    'transition-all duration-200 resize-none'
                  )}
                />
              </div>

              {/* Skills */}
              <div>
                <label className="block text-[11px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">
                  Skills needed
                </label>
                <input
                  type="text"
                  value={skillsInput}
                  onChange={(e) => setSkillsInput(e.target.value)}
                  placeholder="e.g. Typography, CSS, Color theory"
                  disabled={isSubmitting}
                  className={cn(
                    'w-full bg-zinc-800/40 border border-zinc-700/50 rounded-xl px-4 py-2.5',
                    'text-sm text-white placeholder:text-zinc-600',
                    'focus:outline-none focus:ring-1 focus:ring-amber-500/30 focus:border-amber-500/30',
                    'transition-all duration-200'
                  )}
                />
                <p className="text-[11px] text-zinc-600 mt-1">Comma-separated</p>
              </div>

              {/* Challenge type pills */}
              <div>
                <label className="block text-[11px] font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                  Category
                  <span className="text-zinc-600 font-normal normal-case tracking-normal ml-1">— optional, auto-detected if empty</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {CHALLENGE_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setChallengeType(prev => prev === type.id ? '' : type.id)}
                      disabled={isSubmitting}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150',
                        'border',
                        challengeType === type.id
                          ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                          : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                      )}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Collapse details */}
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                <ChevronDown className="w-3 h-3 rotate-180" />
                <span>Hide details</span>
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800/60 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {showDetails && title.trim() && (
              <span className="text-[11px] text-zinc-600 hidden sm:inline">
                <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-500 text-[10px] font-mono">⌘</kbd>
                <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-500 text-[10px] font-mono ml-0.5">↵</kbd>
              </span>
            )}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim()}
              className={cn(
                'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold',
                'transition-all duration-200',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                title.trim()
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 hover:from-amber-400 hover:to-orange-500 active:scale-[0.98]'
                  : 'bg-zinc-800 text-zinc-500'
              )}
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
                  <span>{isEditMode ? 'Saving...' : 'Posting...'}</span>
                </>
              ) : (
                <>
                  <Target className="w-3.5 h-3.5" />
                  <span>{isEditMode ? 'Save Changes' : 'Post Challenge'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Keyframe animations ── */}
        <style>{`
          @keyframes challengeOverlayIn {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes detailsExpand {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}